import React, { useState } from 'react';
import NavigationStepper, {
	NavigationStepperComponentParams,
	NavigationStepperItemType,
} from '../../../../../Components/Stepper/NavigationStepper';
import CaseTypeDetail from '../Detail/CaseTypeDetail';
import styles from './CaseTypeWizard.module.scss';
import CaseTypeOnlineOrdering from '../CaseTypeOnlineOrdering/CaseTypeOnlineOrdering';
import CaseTypeWizardShiftsCheck from './CaseTypeWizardShiftsCheck';
import { useLazyWorkplaceCaseTypeIdQuery } from '../../../../../graphql/graphql';
import useApiClient from '../../../../Base/Hooks/useApiClient';

interface Props {
	workplaceId: string;
	isOnlyOneRoot: boolean;
	onRedirectToShifts: () => void;
	onClose: (forceReload?: boolean) => void;
	onFirstStepSuccess: () => void;
	caseTypeId?: string;
}

const CaseTypeWizard: React.FC<Props> = ({
	workplaceId,
	isOnlyOneRoot,
	onRedirectToShifts,
	caseTypeId: caseTypeIdFromProps,
	onClose,
	onFirstStepSuccess,
}) => {
	const [caseTypeId, setCaseTypeId] = useState<string | undefined>(
		caseTypeIdFromProps,
	);
	const { displayError, displayGQLError } = useApiClient();
	const [getWorkplaceCaseTypeId] = useLazyWorkplaceCaseTypeIdQuery();

	const handleCaseTypeCreateSuccess = async (
		navigationParams: NavigationStepperComponentParams,
		canBookOnline: boolean,
		caseTypeId: string,
	) => {
		onFirstStepSuccess();
		if (canBookOnline) {
			if (!caseTypeIdFromProps) {
				try {
					const result = await getWorkplaceCaseTypeId({ caseTypeId });
					displayGQLError(result);
					if (result?.data?.workplaceCaseType?.id) {
						setCaseTypeId(result.data.workplaceCaseType.id);
						navigationParams.setActiveStepPath('orderSettings');
					}
				} catch (e) {
					displayError(e);
				}
			} else {
				navigationParams.setActiveStepPath('orderSettings');
			}
		} else {
			onClose(true);
		}
	};

	const handleCaseTypeOrderingSettingCreateSuccess = (
		navigationParams: NavigationStepperComponentParams,
	) => {
		navigationParams.setActiveStepPath('shiftSetupNotification');
	};

	const handleOnRemoveCaseType = () => {
		onClose(true);
	};

	const steps: NavigationStepperItemType[] = [
		{
			name: 'component.caseTypeWizard.caseType',
			key: 'caseType',
			component: (params: NavigationStepperComponentParams) => (
				<CaseTypeDetail
					workplaceId={workplaceId}
					isOnlyOneRoot={isOnlyOneRoot}
					onSuccess={async (canBookOnline, caseTypeId) =>
						await handleCaseTypeCreateSuccess(params, canBookOnline, caseTypeId)
					}
					onRemove={handleOnRemoveCaseType}
					caseTypeId={caseTypeId}
				/>
			),
		},
		{
			name: 'component.caseTypeWizard.orderSettings',
			key: 'orderSettings',
			component: (params: NavigationStepperComponentParams) => (
				<CaseTypeOnlineOrdering
					workplaceId={workplaceId}
					caseTypeId={caseTypeId!}
					onSave={() => handleCaseTypeOrderingSettingCreateSuccess(params)}
					addIsOpen={!caseTypeIdFromProps}
				/>
			),
		},
		{
			name: 'component.caseTypeWizard.shiftSetupNotification',
			key: 'shiftSetupNotification',
			component: (params: NavigationStepperComponentParams) => (
				<CaseTypeWizardShiftsCheck
					onClose={onClose}
					onRedirectToShifts={onRedirectToShifts}
					workplaceId={workplaceId}
				/>
			),
		},
	];

	return (
		<NavigationStepper
			steps={steps}
			initialStepPath='caseType'
			navigationClassName={styles.navigation}
		/>
	);
};

export default CaseTypeWizard;
